import Big from "big.js";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import { $deepPopulateItemMetadata, $isOrderUnpaid } from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyServiceChargeToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	serviceCharge,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	serviceCharge: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	orderItems = orderItems.map((orderItem) => {
		const isIncluded =
			!!orderItem.original_price_addons.service_charge.percentage &&
			!!orderItem.original_price_addons.service_charge.amount;
		const serviceChargePercentage = isIncluded
			? orderItem.original_price_addons.service_charge.percentage
			: orderItem.no_service_charge
				? 0
				: serviceCharge;

		const grossSales = bigMath.sub(
			orderItem.subtotal,
			orderItem.subtotal_addons.offer.amount,
			orderItem.subtotal_addons.discount.amount,
		);
		const addedVatScRate = bigMath.add(
			orderItem.original_price_addons.vat.percentage,
			serviceChargePercentage,
		);
		const serviceChargeAmount = new Big(
			new Big(
				new Big(grossSales).sub(
					new Big(grossSales).div(new Big(1).add(addedVatScRate)),
				),
			).mul(serviceChargePercentage),
		)
			.div(addedVatScRate || 1)
			.round(2)
			.toNumber();

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			service_charge: {
				is_included: isIncluded,
				percentage: serviceChargePercentage,
				amount: serviceChargeAmount,
			},
		};

		return { ...orderItem, subtotal_addons };
	});

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const isIncluded =
				!!orderItemOption.original_price_addons.service_charge.percentage &&
				!!orderItemOption.original_price_addons.service_charge.amount;
			const serviceChargePercentage = isIncluded
				? orderItemOption.original_price_addons.service_charge.percentage
				: orderItem.no_service_charge
					? 0
					: serviceCharge;

			const grossSales = bigMath.sub(
				orderItemOption.subtotal,
				orderItemOption.subtotal_addons.offer.amount,
				orderItemOption.subtotal_addons.discount.amount,
			);
			const addedVatScRate = bigMath.add(
				orderItemOption.original_price_addons.vat.percentage,
				serviceChargePercentage,
			);
			const serviceChargeAmount = new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(serviceChargePercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber();

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					service_charge: {
						is_included: isIncluded,
						percentage: serviceChargePercentage,
						amount: serviceChargeAmount,
					},
				},
			};
		}),
	}));

	const service_charge_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.service_charge.amount),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		service_charge_amount,
	}).$order;
};

export const applyServiceChargeToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	serviceCharge,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	serviceCharge: number;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyServiceChargeToOrder({ order, serviceCharge }));
};

export const applyServiceChargeBdToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	serviceChargeBd,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	serviceChargeBd: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	orderItems = orderItems.map((orderItem) => {
		const isIncluded =
			!!orderItem.original_price_addons.service_charge_bd?.percentage &&
			!!orderItem.original_price_addons.service_charge_bd?.amount;
		const serviceChargeBdPercentage = isIncluded
			? (orderItem.original_price_addons.service_charge_bd?.percentage ?? 0)
			: orderItem.no_service_charge
				? 0
				: serviceChargeBd;
		const serviceChargeBdAmount = new Big(orderItem.subtotal)
			.mul(serviceChargeBdPercentage)
			.round(2)
			.toNumber();

		return {
			...orderItem,
			subtotal_addons: {
				...orderItem.subtotal_addons,
				service_charge_bd: {
					is_included: isIncluded,
					percentage: serviceChargeBdPercentage,
					amount: serviceChargeBdAmount,
				},
			},
		};
	});

	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const isIncluded =
				!!orderItemOption.original_price_addons.service_charge_bd?.percentage &&
				!!orderItemOption.original_price_addons.service_charge_bd?.amount;
			const serviceChargeBdPercentage = isIncluded
				? (orderItemOption.original_price_addons.service_charge_bd
						?.percentage ?? 0)
				: orderItem.no_service_charge
					? 0
					: serviceChargeBd;
			const serviceChargeBdAmount = new Big(orderItemOption.subtotal)
				.mul(serviceChargeBdPercentage)
				.round(2)
				.toNumber();

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					service_charge_bd: {
						is_included: isIncluded,
						percentage: serviceChargeBdPercentage,
						amount: serviceChargeBdAmount,
					},
				},
			};
		}),
	}));

	const service_charge_bd_amount = orderItems.reduce(
		(pre, cur) =>
			bigMath.add(pre, cur.subtotal_addons.service_charge_bd.amount),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		service_charge_bd_amount,
	}).$order;
};

export const applyServiceChargeBdToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	serviceChargeBd,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	serviceChargeBd: number;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyServiceChargeBdToOrder({ order, serviceChargeBd }));
};
